import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ViewTransition,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  describeRepeat,
  isNotesChannel,
  type ChannelWithMembership,
  type PublicUser,
  type RichTextDoc,
} from '@avslack/shared';
import { apiFetch } from '../lib/api';
import { isUnreadHeld, releaseUnreadHold } from '../lib/unread-hold';
import { ChannelNotifyMenu } from './ChannelNotifyMenu';
import {
  channelTitle,
  useChannels,
  useJoinChannel,
  useLeaveChannel,
  useMarkRead,
  useMarkUnread,
  useToggleMembershipFlag,
} from '../data/channels';
import {
  ensureMessageLoaded,
  jumpToLatest,
  useDeleteMessage,
  useEditMessage,
  messagePlainText,
  useLoadNewerMessages,
  useLoadOlderMessages,
  useMessages,
  useRetryMessage,
  useSendMessage,
  useToggleImportant,
  useTogglePin,
  useToggleReaction,
  useToggleSaved,
  type LocalMessage,
} from '../data/messages';
import { useUsers } from '../data/users';
import { useSlashCommands } from '../data/commands';
import { dismissEphemeral, useEphemeral } from '../data/ephemeral';
import { useDrafts, useGroups, useSaveDraft, useScheduleMessage } from '../data/workspace';
import { useRealtime } from '../realtime/RealtimeProvider';
import { MessageList } from './MessageList';
import type { MessageActions } from './MessageRow';
import { Composer } from './Composer';
import { TypingIndicator } from './TypingIndicator';
import { PinnedBar } from './PinnedBar';
import { ForwardDialog } from './ForwardDialog';
import {
  MAX_FORWARD_MESSAGES,
  MessageSelectionContext,
  type MessageSelection,
} from '../lib/message-selection';
import { ProjectflowDialog } from './ProjectflowDialog';
import { startProjectflowLink, useProjectflowStatus } from '../data/projectflow';
import { FileBrowser } from './FileBrowser';
import { CanvasPanel } from './CanvasPanel';
import { HuddleBanner, HuddleHeaderButton } from './HuddleControls';
import { primaryCanvases, useCanvases } from '../data/canvases';
import { ChannelMembersDialog } from './ChannelMembersDialog';
import { Icon } from './Icon';
import { ChannelSettingsDialog } from './ChannelSettingsDialog';
import { ChannelSearchPanel } from './ChannelSearchPanel';
import { PollDialog } from './PollDialog';
import { MobileBack } from './MobileBack';
import { ConnectionBanner } from './ConnectionBanner';
import { GuestLinkDialog } from './GuestLinkDialog';
import { TranscriptDialog } from './TranscriptDialog';
import { discardQueued } from '../data/outbox';
import { PhoneChannelBar } from './PhoneChannelBar';
import { PhoneKpis } from './PhoneKpis';
import { phoneLineTitle, useMyPhoneLines } from '../data/phone';
import { useConfirm } from './Confirm';
import { TasksBox } from './TasksBox';
import { applyTaskChange } from '../data/messages';
import { setMessageTask, setMessageTaskDone } from '../lib/api';

/**
 * Ennyi ideig kell latszania egy uzenetnek, hogy olvasottnak szamitson.
 *
 * Nem nulla: egy masik csatornara valtas kozbeni atfuto pillantas kulonben
 * eltuntetne az uj uzenet jelzest, mielott barki elolvasta volna.
 *
 * Viszont HAROM ES FEL masodperc volt, es az sok. Aki megnyitja a csatornat,
 * elolvassa a legfrissebb sort es visszalep, annal a jelzes ottmaradt -
 * ugyanaz az uzenet meg egyszer kiabalt utana. Ennyi ido alatt egy tevedesbol
 * megnyitott csatorna meg nem valik olvasotta, egy tenyleges olvasas viszont
 * mar igen; az egyertelmu szandekra (koppintas az uzenetre) pedig nem is kell
 * varni - lasd {@link ChannelView.handleSeen}.
 */
const READ_DWELL_MS = 800;

interface ChannelViewProps {
  channelId: string;
  me: PublicUser;
  sidebarCollapsed: boolean;
  onExpandSidebar: () => void;
}

export function ChannelView({
  channelId,
  me,
  sidebarCollapsed,
  onExpandSidebar,
}: ChannelViewProps) {
  const { data: channels } = useChannels();
  const { users, usersById, nameDisplay, nameOf } = useUsers();
  const { data: groups } = useGroups();
  const { byChannel: draftsByChannel } = useDrafts();
  const saveDraft = useSaveDraft();
  const scheduleMessage = useScheduleMessage(channelId);
  const { typingByChannel, notifyTyping } = useRealtime();

  // Publikus csatorna elonezete belepes nelkul: ilyenkor nincs a sajat listankban.
  const fromList = channels?.find((c) => c.id === channelId);
  const { data: fetched } = useQuery({
    queryKey: ['channel', channelId],
    queryFn: () =>
      apiFetch<{ channel: ChannelWithMembership }>(`/api/channels/${channelId}`).then(
        (r) => r.channel,
      ),
    enabled: Boolean(channelId) && !fromList,
  });
  const channel = fromList ?? fetched;
  const { data: myPhoneLines } = useMyPhoneLines();

  const { run: runCommand } = useSlashCommands(channelId);
  const messagesQuery = useMessages(channelId);
  const { loadOlder, loading: loadingOlder } = useLoadOlderMessages(channelId);
  const { loadNewer, loading: loadingNewer } = useLoadNewerMessages(channelId);
  const send = useSendMessage(channelId);
  const retry = useRetryMessage(channelId);
  const editMessage = useEditMessage(channelId);
  const removeMessage = useDeleteMessage(channelId);
  const toggleReaction = useToggleReaction(channelId, me.id);
  const markRead = useMarkRead();
  const markUnread = useMarkUnread();
  const confirm = useConfirm();

  /**
   * Kuldes: parancs vagy uzenet?
   *
   * A `/` kezdetu sor csak akkor parancs, ha TENYLEG letezik ilyen nevu
   * parancs. Kulonben rendes uzenetkent megy el - kulonben egy elgepelt
   * "/holnap talalkozunk" csendben eltunne.
   */
  const submit = async (
    value: { text: string; body?: RichTextDoc },
    fileIds: string[],
    isImportant: boolean,
    isTask = false,
  ): Promise<void> => {
    const handled = await runCommand(value.text, fileIds.length === 0, value.body);
    if (handled) return;
    await send(value, me.id, replyingTo?.id ?? null, fileIds, isImportant, isTask);
    /*
      A sajat uzenet utan MINDIG az aljara - most mar kimondva.

      Eddig ezt a lista "az utolso uzenet az enyem" szabalya intezte, ami
      permalink-ablakbol kuldve alulmaradt: kuldes elott kicsereljuk az ablakot
      a legfrissebb oldalra, es a ket gorgetes-szabaly kozul a masik nyert -
      az elkuldott uzenet helyett a lap teteje latszott.
    */
    setScrollToBottomToken((token) => token + 1);
    // Egy valasz egy uzenet: a kovetkezo mar megint sima hozzaszolas.
    setReplyingTo(null);
  };
  const joinChannel = useJoinChannel();
  const leaveChannel = useLeaveChannel();
  const toggleFlag = useToggleMembershipFlag();
  const togglePin = useTogglePin(channelId);
  const toggleImportant = useToggleImportant(channelId);
  const toggleSaved = useToggleSaved(channelId);
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/app/c/$channelId' });
  const navigate = useNavigate();

  const [atBottom, setAtBottom] = useState(true);

  /**
   * Elorben van-e az ablak.
   *
   * A `document.visibilityState` egyszeri leolvasasa nem eleg: ha valaki
   * visszater egy masik fulrol, attol meg nem fut ujra a hatas. Allapotkent
   * kell tartani, hogy a visszateres INDITSA az olvasottsag-orat.
   */
  const [windowVisible, setWindowVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible',
  );

  useEffect(() => {
    const figyel = () => setWindowVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', figyel);
    return () => document.removeEventListener('visibilitychange', figyel);
  }, []);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [scrollToBottomToken, setScrollToBottomToken] = useState(0);

  /**
   * Mire valaszolunk eppen.
   *
   * A MESSENGER mintaja: az idezet a szerkeszto fole kerul, a valasz pedig a
   * folyam vegen jelenik meg. Ezert a valasz allapota ITT el, a szerkeszto
   * mellett - nem az egyes uzeneteknel, ahogy a szalas valtozatban volt.
   */
  const [replyingTo, setReplyingTo] = useState<LocalMessage | null>(null);
  const [forwarding, setForwarding] = useState<LocalMessage[] | null>(null);
  /** Tobb uzenet kijelolese tovabbitashoz (U5): `null` = nincs kijelolo mod. */
  const [selecting, setSelecting] = useState<string[] | null>(null);
  const selection = useMemo<MessageSelection>(
    () => ({
      active: selecting !== null,
      selected: selecting ?? [],
      toggle: (messageId) =>
        setSelecting((current) =>
          current === null
            ? current
            : current.includes(messageId)
              ? current.filter((id) => id !== messageId)
              : current.length >= MAX_FORWARD_MESSAGES
                ? current
                : [...current, messageId],
        ),
    }),
    [selecting],
  );
  // Esc kilep a kijelolo modbol; csatornavaltaskor is megszunik.
  useEffect(() => {
    if (selecting === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelecting(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selecting]);
  useEffect(() => setSelecting(null), [channelId]);
  const [pfMessage, setPfMessage] = useState<LocalMessage | null>(null);
  const { data: projectflow } = useProjectflowStatus();
  const [toast, setToast] = useState<string | null>(null);
  const [filesOpen, setFilesOpen] = useState(false);
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [guestLinkOpen, setGuestLinkOpen] = useState(false);
  const [searchOpen, setSearchOpenNow] = useState(false);
  /*
    A kereso panel NYITASA atmenet: jobbrol becsuszik, zaraskor jobbra ki -
    latszik, hogy a beszelgetes MELLE nyilt, nem helyette.
  */
  const setSearchOpen = (next: boolean | ((open: boolean) => boolean)) =>
    startTransition(() => setSearchOpenNow(next));

  const lastMarkedSeq = useRef(0);

  /**
   * Automatikus olvasottsag-jeloles.
   *
   * Harom feltetel egyszerre:
   *
   *  1. A lista ALJAN allunk - tehat a friss uzenetek tenyleg a kepernyon
   *     vannak. Aki feljebb gorgetve olvas regi dolgokat, annak a kozben
   *     erkezo uj uzenetet nem latta.
   *  2. Az ablak ELOTERBEN van. Egy hattereben nyitva felejtett ful kulonben
   *     mindent elolvasottnak jelolne.
   *  3. Eltelt {@link READ_DWELL_MS} - annyi ido, ami alatt egy uzenetet el
   *     lehet olvasni. Egy atfutó pillantas (masik csatornara valtas kozben)
   *     igy nem tunteti el az uj uzenet jelzest.
   *
   * A varakozas ALATT beerkezo ujabb uzenet ujrainditja az orat: a `target`
   * valtozik, tehat a hatas ujra fut, es a regi idozitot eldobjuk.
   */
  const data = messagesQuery.data;

  /*
    A hatas CSAK egyszeru ertekektol fugg, nem objektumoktol.

    Ez itt nem szepsegkerdes. A `markRead` (mutation-objektum), a `channel` es a
    `data` minden ujrarajzolaskor uj peldany. Ha azok szerepelnenek a
    fuggosegek kozott, a hatas minden rendereskor ujra futna, a takarito
    fuggveny pedig ELDOBNA a meg le nem jart idozitot - es mivel a nezet
    magatol is ujrarajzolodik (gepel valaki, jon egy esemeny), a
    {@link READ_DWELL_MS} soha nem telne le. Ez pontosan igy is tortent, amig
    meg nem mertuk.
  */
  const isMember = channel?.membership.isMember ?? false;
  const lastReadSeq = channel?.membership.lastReadSeq ?? 0;
  const channelLastSeq = data?.channelLastSeq ?? 0;

  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;

  useEffect(() => {
    if (!isMember || !atBottom || !windowVisible) return;
    // Olvatlannak jelolte: amig itt marad, nem jeloljuk vissza (lasd unread-hold.ts).
    if (isUnreadHeld(channelId)) return;
    if (channelLastSeq <= lastReadSeq || channelLastSeq <= lastMarkedSeq.current) return;

    const timer = setTimeout(() => {
      if (isUnreadHeld(channelId)) return;
      lastMarkedSeq.current = channelLastSeq;
      markReadRef.current.mutate({ channelId, seq: channelLastSeq });
    }, READ_DWELL_MS);

    return () => clearTimeout(timer);
  }, [isMember, atBottom, windowVisible, channelId, channelLastSeq, lastReadSeq]);

  useEffect(() => {
    lastMarkedSeq.current = 0;
    // Masik csatornaba lepve az olvatlanna jeloles visszatartasa megszunik.
    return () => releaseUnreadHold(channelId);
  }, [channelId]);

  /**
   * Koppintas az uzenetre: AZONNAL olvasott.
   *
   * A varakozas az akaratlan pillantasok ellen szol - de aki hozzaer egy
   * uzenethez, az nem "atfutott" rajta. Ilyenkor nem kell megvarni az orat.
   *
   * Csak addig a sorig jeloljuk olvasottnak, amelyikhez hozzaert, es nem a
   * csatorna vegeig: aki feljebb gorgetve olvas regi dolgokat, annal az alatta
   * levo friss uzenetek tovabbra is olvasatlanok maradnak. (A szerver amugy is
   * csak NOHET, egy alacsonyabb szam nem vinne vissza a jelzest.)
   */
  const handleSeen = useCallback(
    (seq: number) => {
      if (!isMember || seq <= 0) return;
      if (isUnreadHeld(channelId)) return;
      if (seq <= lastReadSeq || seq <= lastMarkedSeq.current) return;

      lastMarkedSeq.current = seq;
      markReadRef.current.mutate({ channelId, seq });
    },
    [channelId, isMember, lastReadSeq],
  );

  /**
   * Permalink: ha a cimben van uzenet-azonosito, gondoskodunk rola, hogy be
   * legyen toltve (a szerver a kornyezetet adja vissza), majd kiemeljuk.
   */
  useEffect(() => {
    const target = search.m;
    if (!target) return;

    let cancelled = false;
    void ensureMessageLoaded(queryClient, channelId, target).then((found) => {
      if (cancelled) return;
      if (found) setHighlightId(target);
      /*
        A cimbol EGYSZERI hasznalat utan kivesszuk az azonositot.

        Az ugras esemeny, nem allapot: ha a cimben maradna, a csatornara
        visszaterve ujra a regi ablakba rantana a felhasznalot, es ugyanarra a
        kituzott uzenetre masodszor kattintva sem tortenne semmi (a cim nem
        valtozik, a hatas nem indul ujra).
      */
      void navigate({ to: '/c/$channelId', params: { channelId }, search: {}, replace: true });
    });

    return () => {
      cancelled = true;
    };
  }, [search.m, channelId, queryClient, navigate]);

  /*
    VASZON LINKROL (?canvas=...) - a keresesi talalat ide visz.

    Ugyanugy egyszeri, mint az uzenet-permalink: megnyitjuk a panelt ezzel a
    vaszonnal, aztan a cimbol kivesszuk, hogy visszaterve ne nyiljon ujra.
  */
  const [initialCanvasId, setInitialCanvasId] = useState<string | null>(null);
  useEffect(() => {
    const target = search.canvas;
    if (!target) return;
    setInitialCanvasId(target);
    setCanvasOpen(true);
    void navigate({ to: '/c/$channelId', params: { channelId }, search: {}, replace: true });
  }, [search.canvas, channelId, navigate]);

  /*
    ATIRAT LINKROL (?atirat=<huddle>) - a ProjectFlow `call.transcript`
    esemenye ezt a linket viszi a feladatra.

    Egyszeri, mint a tobbi link: megnyitjuk, aztan kivesszuk a cimbol. Az
    `m` (a hivas sora) marad, azt az uzenet-permalink kezeli. Joga a
    szervernek van: aki nem latja az atiratot, annak a parbeszed 404-et kap.
  */
  const [linkedTranscriptId, setLinkedTranscriptId] = useState<string | null>(null);
  useEffect(() => {
    const target = search.atirat;
    if (!target) return;
    setLinkedTranscriptId(target);
    void navigate({
      to: '/c/$channelId',
      params: { channelId },
      search: search.m ? { m: search.m } : {},
      replace: true,
    });
  }, [search.atirat, search.m, channelId, navigate]);

  /*
    Vaszon a csatorna keresojebol. A panel a nyitaskor dont arrol, mit mutat,
    ezert ha mar nyitva van, uj kulccsal ujranyitjuk - kulonben a masik vaszon
    maradna elotte.
  */
  const [canvasPanelKey, setCanvasPanelKey] = useState(0);
  /** Szavazas inditasa (U4). */
  const [pollOpen, setPollOpen] = useState(false);
  const openCanvas = useCallback((canvasId: string) => {
    setInitialCanvasId(canvasId);
    setCanvasPanelKey((key) => key + 1);
    setCanvasOpen(true);
  }, []);

  // A kiemeles atmeneti: figyelemfelhivas, nem allapot.
  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => setHighlightId(null), 2600);
    return () => clearTimeout(timer);
  }, [highlightId]);

  useEffect(() => {
    if (!toast) return;
    // Atmenetkent tunik el: lecsuszik es elhalvanyul - "ez megvolt, elintezodott".
    const timer = setTimeout(() => startTransition(() => setToast(null)), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleTyping = useCallback(() => notifyTyping(channelId), [channelId, notifyTyping]);

  /**
   * Ugras egy talalatra a csatornan beluli keresobol.
   *
   * Ugyanaz az ut, mint a permalinknel: eloszor gondoskodunk rola, hogy az
   * uzenet BE LEGYEN TOLTVE (a szerver a kornyezetet is visszaadja), csak
   * utana emeljuk ki. Forditva a kiemeles egy meg be nem toltott sorra
   * mutatna, es nem tortenne semmi - nema kudarc.
   */
  const jumpToMessage = useCallback(
    (messageId: string) => {
      void ensureMessageLoaded(queryClient, channelId, messageId).then((found) => {
        if (found) setHighlightId(messageId);
      });
    },
    [channelId, queryClient],
  );

  /**
   * Vissza a beszelgetes vegere a permalink-ablakbol.
   *
   * Eloszor a legfrissebb oldalt toltjuk be, es CSAK utana kerjuk a gorgetest
   * az aljara - forditva a meg regi tartalom aljara ugrananak.
   */
  const backToLatest = useCallback(() => {
    setHighlightId(null);
    void jumpToLatest(queryClient, channelId).then(() =>
      setScrollToBottomToken((value) => value + 1),
    );
  }, [channelId, queryClient]);

  const messageActions = useMemo<Omit<MessageActions, 'onOpenReply'>>(
    () => ({
      onToggleReaction: (messageId, emoji) => void toggleReaction(messageId, emoji),
      onEdit: (messageId, value) => editMessage(messageId, value),
      onDelete: (messageId) => {
        void confirm({
          title: 'Üzenet törlése',
          body: 'Az üzenet mindenkinél eltűnik. Ezt nem lehet visszavonni.',
        }).then((ok) => {
          if (ok) void removeMessage(messageId);
        });
      },
      onRetry: (message) => void retry(message),
      onDiscard: (message) => discardQueued(queryClient, message),
      onTogglePin: (messageId) => void togglePin(messageId),
      onToggleImportant: (messageId, isImportant) => {
        void toggleImportant(messageId, isImportant).catch(() =>
          setToast('A jelölést nem sikerült menteni.'),
        );
      },
      onToggleSaved: (messageId) => void toggleSaved(messageId),
      // FELADAT: azonnal latszik, a szerver (es a tobbieknek a WS) megerositi.
      onToggleTask: (messageId, isTask) => {
        applyTaskChange(
          queryClient,
          channelId,
          messageId,
          isTask ? { done: false, doneBy: null, doneAt: null } : null,
        );
        void setMessageTask(messageId, isTask)
          .then((result) => applyTaskChange(queryClient, channelId, messageId, result.task))
          .catch(() => setToast('A feladat-jelölést nem sikerült menteni.'));
      },
      onToggleTaskDone: (messageId, done) => {
        applyTaskChange(queryClient, channelId, messageId, {
          done,
          doneBy: done ? me.id : null,
          doneAt: done ? new Date().toISOString() : null,
        });
        void setMessageTaskDone(messageId, done)
          .then((result) => applyTaskChange(queryClient, channelId, messageId, result.task))
          .catch(() => setToast('Nem sikerült menteni.'));
      },
      onCopyLink: (messageId) => {
        const url = `${window.location.origin}/c/${channelId}?m=${messageId}`;
        void navigator.clipboard
          .writeText(url)
          .then(() => setToast('Link a vágólapon'))
          .catch(() => setToast('A vágólap nem elérhető'));
      },
      /**
       * Az uzenet szovege a vagolapra.
       *
       * A `messagePlainText` a dokumentumbol keszit olvashato sima szoveget -
       * a listak, idezetek es emlitesek is atmennek, nem jelolokent.
       */
      onCopyText: (message) => {
        const text = messagePlainText(message);
        if (!text) return;
        void navigator.clipboard
          .writeText(text)
          .then(() => setToast('Az üzenet a vágólapon'))
          .catch(() => setToast('A vágólap nem elérhető'));
      },
      onStartReply: (message) => setReplyingTo(message),
      onForward: (message) => setForwarding([message]),
      onSelectForForward: (message) => setSelecting([message.id]),
      /*
        UJRAKULDES - ugyanaz az ut, mint egy uj uzenet.

        A szoveg, a formazas es a "fontos" jeloles atjon; a mellekletek
        AZONOSITOVAL mennek, tehat a kep nem toltodik fel ujra - a szerver csak
        egy uj kapcsolatot ir az uzenet es a meglevo fajl koze. Valasz-idezet
        nem jon at: ez uj uzenet a folyam vegen, nem valasz.
      */
      onResend: (message) => {
        void send(
          { text: message.text, body: message.body ?? undefined },
          me.id,
          null,
          message.files.map((file) => file.id),
          message.isImportant,
        ).then(() => setToast('Újraküldve'));
      },
      // A cegnel kikapcsolt integracio nem szurke gomb, hanem nincs ott.
      onProjectflow: projectflow?.enabled ? (message) => setPfMessage(message) : null,
      onMarkUnread: isMember
        ? (message) => {
            void markUnread(channelId, message.seq)
              .then(() => setToast('Olvasatlannak jelölve'))
              .catch(() => {
                releaseUnreadHold(channelId);
                setToast('Nem sikerült olvatlannak jelölni.');
              });
          }
        : null,
    }),
    [
      isMember,
      markUnread,
      toggleReaction,
      editMessage,
      removeMessage,
      retry,
      togglePin,
      toggleSaved,
      confirm,
      channelId,
      toggleImportant,
      projectflow?.enabled,
      send,
      me.id,
      queryClient,
    ],
  );

  if (!channel) {
    return (
      <div className="grid h-full place-items-center text-sm text-ink-muted">
        {messagesQuery.isLoading ? 'Betöltés…' : 'Ez a csatorna nem érhető el.'}
      </div>
    );
  }

  /*
    TELEFONOS CSATORNA: a fejlec alatt a tarcsazo es a cimjegyzek sava all.

    A vonal SAJAT csatornaja a vonal nevet es szamat kapja cimnek ("Telefon –
    Recepció (+36 ...)"). Egy altalanos csatorna (#support), ahova a vonal
    csenget, megtartja a sajat nevet - de a tarcsazot ott is megkapja, hiszen
    ott folynak a hivasok.
  */
  const phoneLines = myPhoneLines?.filter((line) => line.channelId === channel.id) ?? [];
  const ownPhoneLine = phoneLines.find((line) => line.place === 'own') ?? null;
  const title = ownPhoneLine
    ? phoneLineTitle(ownPhoneLine)
    : channelTitle(channel, usersById, me.id, nameDisplay);
  const isRoom = channel.kind === 'public' || channel.kind === 'private';
  /*
    BOTTAL folytatott beszelgetes (pl. a "Naptar" - docs/23): ide irni vagy
    hivast inditani ertelmetlen, senki nem olvassa. A meghivora a kartyan lehet
    valaszolni.
  */
  const botDm =
    channel.kind === 'dm' &&
    (channel.memberIds ?? []).some((id) => id !== me.id && usersById.get(id)?.isBot === true);
  // A sajat jegyzetek: egytagu DM, amiben csak en vagyok.
  const isNotes = channel ? isNotesChannel(channel, me.id) : false;

  /**
   * Fel tud-e venni tagot: admin vagy a csatorna letrehozoja.
   *
   * Ugyanaz a szabaly, mint a szerveren (`requireManageAccess`). A felulet
   * ezzel csak ELREJTI, amit a szerver amugy is elutasitana.
   */
  const canJoin = me.role === 'admin' || me.role === 'owner' || channel.createdBy === me.id;
  const typing = (typingByChannel[channelId] ?? []).map((t) => t.userId);

  return (
    <div className="flex h-full flex-col bg-surface">
      <header className="brand-header flex items-center gap-3 border-b border-border-soft px-4 py-2.5 sm:px-6">
        <MobileBack />

        {sidebarCollapsed && (
          <HeaderButton label="Oldalsáv megnyitása (Ctrl+\)" onClick={onExpandSidebar}>
            <Icon name="jobbra" />
          </HeaderButton>
        )}

        {/*
          A CIM kapja a maradek helyet.

          Enelkul a gombsor (`shrink-0`) elvitte az egeszet, es a cim nulla
          szeles lett - telefonon egyszeruen nem latszott, melyik csatornaban
          all az ember.
        */}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2.5">
            <h1 className="flex min-w-0 items-center gap-1.5 truncate font-display text-lg font-semibold tracking-tight">
              {isRoom && (
                <span aria-hidden="true" className="text-ink-faint">
                  <Icon name={channel.kind === 'private' ? 'lakat' : 'csatorna'} size="sm" />
                </span>
              )}
              <span className="truncate">{title}</span>
              {channel.isArchived && (
                <span className="shrink-0 rounded-pill bg-surface-sunken px-2 py-0.5 text-2xs font-medium text-ink-muted">
                  archiválva
                </span>
              )}
            </h1>

            {/*
              A canvas a CIM MELLE kerul, nem a gombsorba.
              Ott egy ikon volt a sok kozott, es elveszett - pedig a canvas a
              csatorna leirt tudasa, azt keresni kell tudni elso ranezesre.
            */}
            <CanvasHeaderButton channelId={channelId} onOpen={() => setCanvasOpen(true)} />
          </div>
          {channel.topic && <p className="truncate text-xs text-ink-muted">{channel.topic}</p>}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1">
          {/*
            A tagszam MAGA a belepesi pont a tagsaghoz.
            Aki azt kerdezi, "ki van bent?", az erre a szamra nez - kar lenne
            egy kulon ikon moge rejteni a valaszt.
          */}
          {isRoom && (
            <button
              type="button"
              onClick={() => setMembersOpen(true)}
              title="A csatorna tagjai"
              className="mr-1 hidden rounded-control px-1.5 py-1 text-xs text-ink-faint transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink sm:inline"
            >
              {channel.memberCount} tag
            </button>
          )}

          {/* A huddle a csatorna elso szamu cselekvese - a fajlok ele kerul. */}
          {channel.membership.isMember && !channel.isArchived && !botDm && (
            <HuddleHeaderButton channelId={channelId} />
          )}

          {/* FELADATOK: kis doboz, ha van feladatnak jelolt uzenet - lenyitva kipipalhato. */}
          <TasksBox channelId={channelId} />

          {/* A csatorna naptara (H1): utemezett hivasok es esemenyek. */}
          {isRoom && channel.membership.isMember && (
            <HeaderButton
              mobilonRejtve
              label="A csatorna naptára"
              onClick={() =>
                void navigate({ to: '/naptar', search: { forras: `channel:${channelId}` } })
              }
            >
              <Icon name="naptar" />
            </HeaderButton>
          )}

          {/* Vendeglink (H2): kulso fel meghivasa a hivasba - elore is kuldheto. */}
          {channel.membership.isMember && !channel.isArchived && !botDm && me.role !== 'guest' && (
            <HeaderButton
              mobilonRejtve
              label="Vendég meghívása"
              onClick={() => setGuestLinkOpen(true)}
            >
              <Icon name="link" />
            </HeaderButton>
          )}

          <HeaderButton mobilonRejtve label="Fájlok" onClick={() => setFilesOpen(true)}>
            <Icon name="csatolmany" />
          </HeaderButton>

          {channel.membership.isMember && (
            <>
              {/*
                MOBILON elrejtve: a csillagozas, a nemitas es a beallitasok
                ritka muveletek, a hely viszont a cimnek kell. Mindharom
                elerheto maradt asztalon es a csatorna beallitasaiban.
              */}
              <HeaderButton
                mobilonRejtve
                label={channel.membership.isStarred ? 'Csillagozás levétele' : 'Csillagozás'}
                active={channel.membership.isStarred}
                onClick={() =>
                  toggleFlag.mutate({
                    channelId,
                    patch: { isStarred: !channel.membership.isStarred },
                  })
                }
              >
                <Icon name="csillag" />
              </HeaderButton>

              {/* Ertesitesi szint (mindig / soha / ...) es nemitas - egy menuben. */}
              <ChannelNotifyMenu channel={channel} />

              {/*
                BEALLITASOK.

                A jogot a szerver ellenorzi (`requireManageAccess`), ezert a
                gombot mindenkinek megmutatjuk - aki nem jogosult, vilagos
                hibauzenetet kap. Egy elrejtett gomb azt sugallna, hogy a
                funkcio nem letezik.
              */}
              {/*
                KERESES EBBEN A BESZELGETESBEN.

                A kozos kereso-oldal egy masik nezetbe visz; a leggyakoribb
                kerdes viszont az, hogy "hol is volt ez ITT" - arra ez
                valaszol, anelkul hogy elhagynad a beszelgetest.
              */}
              <HeaderButton
                label="Keresés ebben a beszélgetésben"
                active={searchOpen}
                onClick={() => setSearchOpen((open) => !open)}
              >
                <Icon name="keres" />
              </HeaderButton>

              {isRoom && (
                <HeaderButton
                  mobilonRejtve
                  label="Csatorna beállításai"
                  onClick={() => setSettingsOpen(true)}
                >
                  <Icon name="beallitasok" />
                </HeaderButton>
              )}

              {isRoom && !channel.isGeneral && (
                <button
                  type="button"
                  onClick={async () => {
                    // Privat csatornaba csak ujabb meghivassal lehet visszajutni.
                    const ok = await confirm({
                      title: 'Kilépés a csatornából',
                      body:
                        channel.kind === 'private'
                          ? 'Ez privát csatorna: csak akkor jutsz vissza, ha valaki újra meghív.'
                          : 'Bármikor visszaléphetsz a Csatornák böngészéséből.',
                      confirmLabel: 'Kilépés',
                    });
                    if (ok) leaveChannel.mutate(channelId);
                  }}
                  title="Kilépés a csatornából"
                  className="rounded-control px-2.5 py-1.5 text-xs text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
                >
                  Kilépés
                </button>
              )}
            </>
          )}
        </div>
      </header>
      <ConnectionBanner />
      <GuestLinkDialog channelId={channelId} open={guestLinkOpen} onOpenChange={setGuestLinkOpen} />
      {linkedTranscriptId && (
        <TranscriptDialog
          huddleId={linkedTranscriptId}
          open
          onOpenChange={(open) => {
            if (!open) setLinkedTranscriptId(null);
          }}
        />
      )}

      {settingsOpen && (
        <ChannelSettingsDialog channel={channel} onClose={() => setSettingsOpen(false)} />
      )}

      <ChannelMembersDialog
        open={membersOpen}
        onOpenChange={setMembersOpen}
        channel={channel}
        me={me}
      />

      {phoneLines.length > 0 && <PhoneChannelBar channelId={channelId} meId={me.id} />}
      {/* Keskenyebb kepernyon a mai szamok egy savban; szelesen oldalt (lent). */}
      {phoneLines.length > 0 && (
        <div className="xl:hidden">
          <PhoneKpis channelId={channelId} variant="strip" />
        </div>
      )}

      <HuddleBanner channelId={channelId} meId={me.id} />
      {/*
        A KERESO-PANEL a beszelgetes MELLE kerul, nem fole.

        Lebego retegkent egyszerubb lenne, de eppen azt takarna el, amit a
        talalatra kattintva meg kell nezni. Igy a beszelgetes keskenyebb
        lesz, viszont vegig latszik - a kiemelt uzenettel egyutt.
      */}
      {/*
        A `min-w-0` NEM diszites, es nem is elovigyazatossag.

        Egy flex-elem alapertelmezett `min-width: auto` erteke megtiltja, hogy
        a TARTALMANAL keskenyebb legyen. Egy szeles kodblokk, egy hosszu URL
        vagy egy kep igy nem zsugorodik, hanem szetfesziti a sajat oszlopat -
        telefonon merve 386 keppontos nezetben 498 keppontes oszlopot kaptunk,
        es a tartalom jobb szele egyszeruen levagodott.

        Asztalon ez nem latszott: ott van hova szelesedni.
      */}
      <div className="flex min-h-0 min-w-0 flex-1">
        {/*
          TELEFONOS CSATORNA: a mai szamok az uzenetfolyam bal oldalan. Szeles
          kepernyon ott ugyis ures sav van (a folyam kozepre igazodik).
        */}
        {phoneLines.length > 0 && <PhoneKpis channelId={channelId} variant="side" />}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <PinnedBar channelId={channelId} meId={me.id} usersById={usersById} />

          <MessageSelectionContext.Provider value={selection}>
            <MessageList
              key={channelId}
              channelId={channelId}
              isDirect={!isRoom}
              isNotes={isNotes}
              messages={data?.messages ?? []}
              offline={data?.offline === true}
              usersById={usersById}
              meId={me.id}
              canManage={me.role === 'admin' || me.role === 'owner'}
              lastReadSeq={channel.membership.lastReadSeq}
              hasMore={data?.hasMore ?? false}
              loadingOlder={loadingOlder}
              onLoadOlder={() => void loadOlder()}
              hasMoreNewer={data?.hasMoreNewer ?? false}
              loadingNewer={loadingNewer}
              onLoadNewer={() => void loadNewer()}
              onJumpToLatest={backToLatest}
              scrollToBottomToken={scrollToBottomToken}
              onAtBottomChange={setAtBottom}
              // Ugras az olvasatlanra: ugyanaz az ut, mint a permalinkne - gorget
              // es atmenetileg kiemeli, hogy a szem megtalalja.
              onJumpTo={setHighlightId}
              onSeen={handleSeen}
              actions={messageActions}
              highlightId={highlightId}
            />
          </MessageSelectionContext.Provider>

          {/* KIJELOLO MOD (U5): hany uzenet van kijelolve, es a tovabbitas. */}
          {selecting && (
            <div className="mx-4 mb-2 flex items-center gap-3 rounded-card border border-accent bg-accent-soft px-3 py-2 text-sm sm:mx-6">
              <span className="min-w-0 flex-1">
                <strong>{selecting.length}</strong> kijelölve
                <span className="text-ink-muted"> (legfeljebb {MAX_FORWARD_MESSAGES})</span>
              </span>
              <button
                type="button"
                disabled={selecting.length === 0}
                onClick={() => {
                  const chosen = (data?.messages ?? []).filter((m) => selecting.includes(m.id));
                  setSelecting(null);
                  setForwarding(chosen);
                }}
                className="rounded-control bg-accent px-3 py-1.5 text-xs font-semibold text-accent-ink hover:bg-accent-hover disabled:opacity-50"
              >
                Továbbítás
              </button>
              <button
                type="button"
                onClick={() => setSelecting(null)}
                className="rounded-control px-2 py-1.5 text-xs text-ink-muted hover:bg-surface-hover"
              >
                Mégse
              </button>
            </div>
          )}

          <TypingIndicator userIds={typing} />

          {channel.membership.isMember ? (
            <>
              <EphemeralNotes channelId={channelId} />

              <Composer
                /*
              Csatornankent KULON szerkeszto.

              Csatornavaltaskor ujraepul (uj piszkozat, uj tartalom), egy
              csatornan belul viszont soha - igy iras kozben nem veszhet el a
              fokusz.
            */
                key={channelId}
                channelId={channelId}
                placeholder={`Üzenet ide: ${isRoom ? (channel.kind === 'private' ? '⌾' : '#') : ''}${title}`}
                users={users}
                groups={groups ?? []}
                meId={me.id}
                disabled={channel.isArchived || botDm}
                disabledReason={
                  botDm
                    ? 'Ez egy automatikus értesítési csatorna – a meghívókra a kártyán tudsz válaszolni.'
                    : 'Ez a csatorna archiválva van, ide már nem lehet írni.'
                }
                initialDraft={draftsByChannel.get(channelId)?.body ?? null}
                replyingTo={replyingTo}
                onCancelReply={() => setReplyingTo(null)}
                onSend={(value, fileIds, isImportant, isTask) =>
                  void submit(value, fileIds, isImportant, isTask)
                }
                onTyping={handleTyping}
                onDraftChange={(value) => void saveDraft(channelId, value)}
                onPoll={() => setPollOpen(true)}
                onSchedule={async (value, sendAt, isImportant, repeat) => {
                  const scheduled = await scheduleMessage.mutateAsync({
                    text: value.text,
                    body: value.body,
                    sendAt: sendAt.toISOString(),
                    isImportant,
                    ...(repeat ? { repeat } : {}),
                  });
                  setToast(
                    repeat
                      ? `Ismétlődő üzenet: ${describeRepeat(sendAt, scheduled.repeat).toLowerCase()}`
                      : `Ütemezve: ${sendAt.toLocaleString('hu-HU', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}`,
                  );
                }}
              />
            </>
          ) : (
            <div className="px-4 pb-4 sm:px-6">
              <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 rounded-card border border-border-soft bg-surface-raised px-4 py-3 shadow-(--shadow-soft)">
                <p className="text-sm text-ink-muted">Előnézetben nézed ezt a csatornát.</p>
                {/*
              BELEPNI NEM LEHET, csak felvenni.

              A szerver a csatlakozast kezeloi joghoz koti (admin vagy a
              csatorna letrehozoja). A gomb eddig mindenkinek itt volt, es
              kattintasra hibauzenettel allt meg - egy gomb, ami mindig
              elutasit, azt tanitja, hogy a felulet nem mond igazat.
            */}
                {canJoin ? (
                  <button
                    type="button"
                    onClick={() => joinChannel.mutate(channelId)}
                    disabled={joinChannel.isPending}
                    className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover disabled:opacity-50"
                  >
                    Csatlakozás
                  </button>
                ) : (
                  <p className="text-xs text-ink-faint">
                    A tagságot admin vagy a csatorna létrehozója adja.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {searchOpen && (
          <ViewTransition enter="slide-from-right" exit="slide-to-right" default="none">
            <ChannelSearchPanel
              channelId={channelId}
              onJump={jumpToMessage}
              onOpenCanvas={openCanvas}
              onClose={() => setSearchOpen(false)}
            />
          </ViewTransition>
        )}
      </div>

      {/* A live regio MINDIG a DOM-ban van - kulonben a kepernyoolvaso nem mindig olvassa fel. */}
      <div
        role="status"
        className="pointer-events-none fixed bottom-24 left-1/2 z-50 -translate-x-1/2"
      >
        {toast && (
          <ViewTransition exit="toast-out" default="none">
            <p className="animate-fade-rise rounded-pill bg-ink px-4 py-2 text-sm font-medium text-surface shadow-(--shadow-modal)">
              {toast}
            </p>
          </ViewTransition>
        )}
      </div>

      {pollOpen && <PollDialog channelId={channelId} onClose={() => setPollOpen(false)} />}

      {canvasOpen && (
        /*
          A megnyitast a panel sajat CSS-animacioja viszi (fade-rise); a
          BEZARAS eddig egy pillanat alatt tunt el. Atmenetkent zarva a panel
          lathatoan visszahuzodik, es latszik, hova tert vissza a figyelem.
        */
        <ViewTransition exit="panel-close" default="none">
          <CanvasPanel
            key={canvasPanelKey}
            channelId={channelId}
            initialCanvasId={initialCanvasId}
            channelLabel={isRoom ? `#${channel.name}` : title}
            me={me}
            canEdit={channel.membership.isMember && !channel.isArchived}
            personal={isNotes}
            onClose={() =>
              startTransition(() => {
                setCanvasOpen(false);
                setInitialCanvasId(null);
              })
            }
          />
        </ViewTransition>
      )}

      {filesOpen && (
        <FileBrowser
          channelId={channelId}
          channelLabel={isRoom ? `#${channel.name}` : title}
          onClose={() => setFilesOpen(false)}
        />
      )}

      {forwarding && (
        <ForwardDialog
          items={forwarding.map((message) => ({
            message,
            authorName: (message.userId ? nameOf(message.userId, '') : null) || 'Ismeretlen',
          }))}
          sourceChannelName={isRoom ? `#${channel.name}` : title}
          meId={me.id}
          onClose={() => setForwarding(null)}
        />
      )}

      {pfMessage && (
        <ProjectflowDialog
          source={{ messageId: pfMessage.id }}
          linked={Boolean(projectflow?.linked)}
          onClose={() => setPfMessage(null)}
          onLink={startProjectflowLink}
        />
      )}
    </div>
  );
}

function HeaderButton({
  label,
  active,
  mobilonRejtve,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  /**
   * Telefonon nem jelenik meg.
   *
   * A fejlec ott 386 keppont szeles: minden gomb 36-ot visz el a CSATORNA
   * NEVE elol. A ritkan hasznalt muveletek ezert asztalra maradnak - a
   * mindennapiak (ölelés, keresés) telefonon is ott vannak.
   */
  mobilonRejtve?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={[
        'size-8 place-items-center rounded-control transition-colors duration-(--duration-quick)',
        mobilonRejtve ? 'hidden sm:grid' : 'grid',
        active
          ? 'bg-accent-soft text-accent'
          : 'text-ink-faint hover:bg-surface-hover hover:text-ink',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

/**
 * Efemer valaszok a szerkesztosav folott.
 *
 * Csak nekem latszanak, nem kerulnek az elozmenybe, es egy perc mulva
 * maguktol eltunnek. Igy egy parancs tud olyat valaszolni, ami senki mast
 * nem erdekel, anelkul hogy a csatornat teleirna.
 */
function EphemeralNotes({ channelId }: { channelId: string }) {
  const notes = useEphemeral(channelId);

  // A burkolo ures listanal is marad: a live regionak a szoveg ELOTT kell leteznie.
  return (
    <div role="status">
      {notes.length > 0 && (
        <div className="space-y-1.5 px-4 pb-2 sm:px-6">
          {notes.map((note) => (
            <div
              key={note.id}
              className="animate-fade-rise flex items-start gap-2 rounded-card border border-border-soft bg-surface-sunken px-3 py-2 text-xs"
            >
              <code className="shrink-0 font-mono text-2xs text-ink-faint">{note.source}</code>
              <p className="min-w-0 flex-1 whitespace-pre-wrap">{note.text}</p>
              <button
                type="button"
                aria-label="Elrejtés"
                onClick={() => dismissEphemeral(note.id)}
                className="shrink-0 rounded-control px-1 text-ink-faint transition-colors hover:text-ink"
              >
                ×
              </button>
            </div>
          ))}
          <p className="text-2xs text-ink-faint">Csak te látod. Egy perc múlva eltűnik.</p>
        </div>
      )}
    </div>
  );
}

/**
 * A canvas-gomb a fejlecben.
 *
 * KIEMELT, feliratos pirula a csatorna neve mellett - nem egy halvany ikon a
 * gombsorban, ahol elveszett. Az akcentus lagy arnyalata: feltunik, de nem
 * versenyez az öleles teli akcentus-gombjaval.
 *
 * A darabszam AZERT latszik, mert canvas nelkul a gomb nem mond semmit: igy
 * egy pillantasbol kiderul, van-e egyaltalan mit megnyitni. A kiemelt canvas
 * cime a buborekban jelenik meg, hogy ne kelljen megnyitni a talalgatashoz.
 */
function CanvasHeaderButton({ channelId, onOpen }: { channelId: string; onOpen: () => void }) {
  const { data: canvases } = useCanvases(channelId);
  const primary = primaryCanvases(canvases);
  const count = canvases?.length ?? 0;
  const label = primary.length
    ? `Kiemelt canvas: ${primary.map((canvas) => canvas.title).join(', ')}`
    : 'Canvasok';

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onOpen}
      className={[
        'flex shrink-0 items-center gap-1.5 rounded-pill border px-2.5 py-1 text-xs font-semibold',
        'border-accent/30 bg-accent-soft text-accent',
        'transition-[border-color,box-shadow,scale] duration-(--duration-quick) hover:border-accent/60 hover:shadow-(--shadow-soft)',
        'active:scale-95',
      ].join(' ')}
    >
      <Icon name="canvas" size="sm" />
      {/* Mobilon csak ikon + szam: a hely a csatorna nevenek kell. */}
      <span className="hidden sm:inline">Canvasok</span>
      {count > 0 && (
        <span className="rounded-pill bg-accent px-1.5 text-2xs leading-4 text-accent-ink">
          {count}
        </span>
      )}
    </button>
  );
}
